/**
 * BSA — Page "Mes Bassins"
 * Layout 2 colonnes · Mockup approuvé 17 avr. 2026.
 * Depends on bsa-api.js and bsa-pages.css.
 */

(function () {
  "use strict";

  const TYPES = { pool: "Piscine", spa: "Spa" };
  const DISINFECTANTS = { chlorine: "Chlore", bromine: "Brome", salt: "Sel" };
  const FILTERS = { sand: "Sable", cartridge: "Cartouche", de: "Diatomée" };
  const UNITS = { litres: "Litres", gallons: "Gallons" };

  const CONSTRUCTIONS = [
    { val: "fibre_de_verre", label: "Fibre de verre" },
    { val: "vinyle",         label: "Vinyle" },
    { val: "beton",          label: "Béton" },
  ];
  const COUVERTURES = [
    { val: "automatise",    label: "Couvert automatisé" },
    { val: "toile_solaire", label: "Toile solaire" },
  ];

  const POOL_KEYS = ["type_construction", "piscine_couverte", "type_couverture"];

  // Clés "type de chlore" dans produits_utilises_json (parité POS Modal.jsx).
  const CHLORINE_TYPE_KEYS = ["pucks_trichlor", "dichlor_shock", "granules_non_stab"];
  // parseProductsUsedKeys : normalise produits_utilises_json (string JSON OU array) en tableau. Seul try/catch centralisé.
  function parseProductsUsedKeys(raw) {
    try {
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        const parsed = JSON.parse(raw || "[]");
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (_) {}
    return [];
  }

  const ANALYSE_VALUES = {
    hardness: [0, 100, 150, 250, 500, 1000],
    fc:       [0, 0.5, 1, 3, 5, 10],
    br:       [0, 1, 2, 6, 10, 20],
    cya:      [0, 40, 100, 150, 240],
    alk:      [0, 40, 80, 120, 180, 240],
    ph:       [6.2, 6.8, 7.2, 7.8, 8.4, 9.0]
  };
  const ANALYSE_TARGETS = {
    pool: {
      hardness: { min: 150, max: 500 },
      alk:      { min: 80,  max: 160 },
      ph:       { min: 7.2, max: 7.8 },
      fc:       { min: 1,   max: 3   },
      cya:      { min: 30,  max: 50  },
    },
    spa: {
      hardness: { min: 150, max: 500 },
      alk:      { min: 80,  max: 160 },
      ph:       { min: 7.2, max: 7.8 },
      fc:       { min: 3,   max: 6   },
      br:       { min: 3,   max: 6   },
    }
  };
  const ANALYSE_PARAM_LABELS = {
    ph:       { label: 'pH',                unit: '' },
    alk:      { label: 'Alcalinité (TA)',   unit: 'ppm' },
    hardness: { label: 'Dureté calcique',   unit: 'ppm' },
    fc:       { label: 'Chlore libre',      unit: 'ppm' },
    br:       { label: 'Brome',             unit: 'ppm' },
    cya:      { label: 'Stabilisant (CYA)', unit: 'ppm' },
  };
  const ETAT_LABELS = {
    crystal:    { label: 'Cristalline', cls: 'ok'   },
    cloudy:     { label: 'Brouillée',   cls: 'warn' },
    lightalgae: { label: 'Colorée',     cls: 'warn' },
    algae:      { label: 'Algues',      cls: 'crit' },
  };

  const renderLsiDial = (lsi) =>
    (window.BSA_LSI && window.BSA_LSI.renderLsiDial)
      ? window.BSA_LSI.renderLsiDial(lsi)
      : '';

  function getParamValue(key, idx) {
    const arr = ANALYSE_VALUES[key];
    if (!arr || idx === null || idx === undefined || idx === '') return null;
    const n = parseFloat(idx);
    if (!isFinite(n)) return null;
    if (Number.isInteger(n) && arr[n] !== undefined) return arr[n];
    const low = Math.floor(n), high = Math.ceil(n);
    if (arr[low] !== undefined && arr[high] !== undefined)
      return (arr[low] + arr[high]) / 2;
    return null;
  }

  function getBand(key, value, waterType) {
    if (value === null) return 'NA';
    const t = (ANALYSE_TARGETS[waterType] || ANALYSE_TARGETS.pool)[key];
    if (!t) return 'NA';
    if (value < t.min) return 'LOW';
    if (value > t.max) return 'HIGH';
    return 'OK';
  }

  function bandClass(band) {
    if (band === 'LOW' || band === 'HIGH') return 'warn';
    if (band === 'NA') return 'info';
    return 'ok';
  }

  function bandLabel(band) {
    if (band === 'LOW') return 'BAS';
    if (band === 'HIGH') return 'ÉLEVÉ';
    if (band === 'NA') return 'N/A';
    return 'OK';
  }

  let bassins = [];
  let analyses = [];
  let selectedId = null;
  let isNewForm = false;
  let customerName = "";
  let customerAddress = "";

  const container = document.getElementById("bsa-bassins-app");
  if (!container) return;

  const customerId = container.dataset.customerId;
  const mascotteUrl = container.dataset.mascotteUrl || "";

  if (!customerId) {
    container.innerHTML = `
      <div class="bsa-login-prompt">
        <h2>Connectez-vous</h2>
        <p>Vous devez être connecté pour gérer vos bassins.</p>
        <a href="/account/login" class="bsa-btn bsa-btn--primary">Se connecter</a>
      </div>
    `;
    return;
  }

  // ─── Init ─────────────────────────────────────────────────────

  async function init() {
    showLoading();
    window.BSA_API.init({ customerId });

    document.addEventListener("bsa:customer-loaded", onCustomerLoaded);

    try {
      const [bList, aRes] = await Promise.all([
        window.BSA_API.getBassins(),
        window.BSA_API.getAnalyses({ limit: 5 }).catch(() => null),
      ]);
      bassins = bList || [];
      analyses = (aRes && (aRes.analyses || aRes)) || [];

      if (analyses.length > 0) {
        console.log('[BSA] Structure analyse[0]:', analyses[0]);
        console.log('[BSA] Liste bassins IDs:', bassins.map((b) => b.id));
      }

      selectedId = null;
      isNewForm = false;

      render();
    } catch (e) {
      container.innerHTML = '<p style="text-align:center;color:#d63b3b;font-weight:700;">Erreur de chargement.</p>';
      console.error("[BSA] init error:", e);
    }
  }

  function onCustomerLoaded(ev) {
    const p = ev.detail || {};

    const fullName = [p.first_name || p.firstName, p.last_name || p.lastName]
      .filter(Boolean).join(" ") || p.display_name || p.name || p.email || "Client";

    const addr = p.default_address || p.address || {};
    const addressParts = [
      addr.address1 || addr.street,
      addr.city,
      addr.province_code || addr.province,
    ].filter(Boolean);
    const addressStr = addressParts.join(", ");

    customerName = fullName;
    customerAddress = addressStr;

    const badge = document.getElementById("bsa-customer-badge");
    if (badge) {
      badge.innerHTML = addressStr
        ? `<strong>${esc(fullName)}</strong><br><span style="opacity:0.7;font-size:11px">${esc(addressStr)}</span>`
        : esc(fullName);
    }
  }

  // ─── Render ───────────────────────────────────────────────────

  function render() {
    container.innerHTML = `
      <div class="bsa-mb">
        ${renderTopbar()}
        <div class="bsa-mb-layout">
          <section class="bsa-mb-left">
            ${renderBassinList()}
            ${renderAnalyseList()}
          </section>
          <aside class="bsa-mb-right">
            ${renderForm()}
          </aside>
        </div>
        ${renderMascotte()}
      </div>
    `;
    bindEvents();
  }

  function renderTopbar() {
    const count = bassins.length;
    const countLabel = `${count} ${count === 1 ? "bassin" : "bassins"}`;
    const badge = customerName || "Client";
    const badgeHtml = customerAddress
      ? `<strong>${esc(badge)}</strong><br><span style="opacity:0.7;font-size:11px">${esc(customerAddress)}</span>`
      : esc(badge);
    return `
      <header class="bsa-topbar">
        <div class="bsa-topbar-left">
          <h1 class="bsa-topbar-title">Mes bassins</h1>
          <div class="bsa-topbar-sub">Gestion de vos bassins et analyses d'eau</div>
        </div>
        <div class="bsa-topbar-right">
          <span class="bsa-badge-navy" id="bsa-customer-badge" style="text-align:right;line-height:1.3;padding:6px 14px;">${badgeHtml}</span>
          <span class="bsa-count">${countLabel}</span>
        </div>
      </header>
    `;
  }

  function renderBassinList() {
    let html = `<section>
      <h2 class="bsa-section-title">Mes bassins</h2>`;

    if (bassins.length === 0) {
      html += `
        <div class="bsa-empty" style="padding:32px 16px;">
          <div class="bsa-empty__title">Aucun bassin enregistré</div>
          <div class="bsa-empty__text">Ajoutez votre piscine ou spa pour sauvegarder vos analyses.</div>
        </div>
      `;
    } else {
      html += `<div class="bsa-bassin-list">`;
      for (const b of bassins) {
        const isSelected = b.id === selectedId && !isNewForm;
        const isPrincipal = b.bassin_principal === "true";
        const name = b.nom_du_bassin || (b.type_d_eau === "spa" ? "Mon spa" : "Ma piscine");
        const detail = [
          TYPES[b.type_d_eau] || b.type_d_eau,
          DISINFECTANTS[b.desinfectant] || b.desinfectant,
          b.volume ? `${Math.round(parseFloat(b.volume))} ${UNITS[b.unite_de_volume] || ""}` : null,
        ].filter(Boolean).join(" · ");

        html += `
          <div class="bsa-bassin-item${isSelected ? " active" : ""}" data-select-bassin="${esc(b.id)}">
            <span class="bsa-bassin-dot${isPrincipal ? "" : " off"}"></span>
            <div class="bsa-bassin-body">
              <div class="bsa-bassin-name">${esc(name)}</div>
              <div class="bsa-bassin-detail">${esc(detail)}</div>
            </div>
            <div class="bsa-bassin-actions">
              <button type="button" class="bsa-pill-btn" data-edit-bassin="${esc(b.id)}">Modifier</button>
              <a href="/pages/water-analyzer?bassin=${encodeURIComponent(b.id)}" class="bsa-btn-analyse-main">Analyser →</a>
            </div>
          </div>
        `;
      }
      html += `</div>`;
    }

    html += `<button type="button" class="bsa-btn-add" id="bsa-add-bassin-btn">+ Ajouter un bassin</button>`;
    html += `</section>`;
    return html;
  }

  function renderAnalyseList() {
    if (!analyses || analyses.length === 0) return "";

    let filtered = analyses;
    if (selectedId && !isNewForm) {
      filtered = analyses.filter((a) => {
        const aBassinId = a.bassin_reference || a.bassin?.id || a.bassin_id;
        return aBassinId === selectedId;
      });
    }

    if (filtered.length === 0 && selectedId && !isNewForm) {
      return `
        <section>
          <h2 class="bsa-section-title">Analyses de ce bassin</h2>
          <div class="bsa-mb-form__empty" style="padding:20px;">
            Aucune analyse pour ce bassin. <a href="/pages/water-analyzer?bassin=${encodeURIComponent(selectedId)}" style="color:var(--accent);text-decoration:underline;">Lancer une analyse →</a>
          </div>
        </section>
      `;
    }

    const sectionTitle = selectedId && !isNewForm
      ? "Analyses de ce bassin"
      : "Dernières analyses — tous bassins";

    const fallbackName = (b) =>
      b?.nom_du_bassin || (b?.type_d_eau === "spa" ? "Mon spa" : "Ma piscine");

    let html = `
      <section>
        <h2 class="bsa-section-title">${sectionTitle}</h2>
        <div class="bsa-bassin-list">
    `;

    for (const a of filtered.slice(0, 10)) {
      const bassinId = a.bassin_reference || a.bassin?.id || a.bassin_id || null;
      const matchedBassin = bassinId ? bassins.find((b) => b.id === bassinId) : null;
      const bassinName = fallbackName(matchedBassin)
        || a.bassin_nom
        || a.bassin?.nom_du_bassin
        || a.nom_du_bassin
        || "—";

      const date = fmtDate(a.date_d_analyse || a.date || a.created_at);
      const score = a.score_qualite != null ? parseInt(a.score_qualite, 10)
                  : (a.score != null ? parseInt(a.score, 10) : null);

      let badgeClass = "ok", badgeLabel = "—";
      if (score != null && !isNaN(score)) {
        badgeLabel = `${score}%`;
        if (score >= 80)      badgeClass = "ok";
        else if (score >= 60) badgeClass = "warn";
        else                  badgeClass = "crit";
      }

      const lsiPill = (() => {
        const v = parseFloat(a.lsi);
        if (!isFinite(v)) return "";
        const cls = Math.abs(v) <= 0.3 ? "ok" : (Math.abs(v) <= 0.5 ? "warn" : "crit");
        return `<span class="bsa-analyse-badge ${cls}">LSI ${v.toFixed(2)}</span>`;
      })();
      html += `
        <div class="bsa-analyse-row" data-open-analyse="${esc(a.id || "")}">
          <div class="bsa-analyse-info">
            <div class="bsa-analyse-name">${esc(bassinName)}</div>
            <div class="bsa-analyse-date">${esc(date)}</div>
          </div>
          ${lsiPill}
          <span class="bsa-analyse-badge ${badgeClass}">${esc(badgeLabel)}</span>
        </div>
      `;
    }
    html += `</div></section>`;
    return html;
  }

  function renderForm() {
    const b = isNewForm ? null : bassins.find((x) => x.id === selectedId);

    if (!b && !isNewForm) {
      return '';
    }

    const typeEau     = b?.type_d_eau || "pool";
    const disinf      = b?.desinfectant || "chlorine";
    const unit        = b?.unite_de_volume || "litres";
    const filtre      = b?.tpe_de_filtre || "";
    const construction= b?.type_construction || "";
    const couverte    = b?.piscine_couverte === "true";
    const couverture  = b?.type_couverture || "";
    const principal   = b?.bassin_principal === "true";
    const ionisateur  = b?.ionisateur === "true";
    const ozonateur   = b?.ozonateur === "true";
    // Type de chlore depuis produits_utilises_json existant (parité POS) : granules_non_stab → "granules", sinon "pucks".
    const chlorineType = parseProductsUsedKeys(b?.produits_utilises_json ?? b?.produits_utilises).includes("granules_non_stab") ? "granules" : "pucks";

    return `
      <div class="bsa-mb-form" id="bsa-bassin-form">
        <div class="bsa-mb-form-group">
          <label>Nom du bassin</label>
          <input type="text" id="bf-name" value="${esc(b?.nom_du_bassin || "")}" placeholder="Ex: Ma piscine creusée">
        </div>

        <div class="bsa-mb-form-group">
          <label>Type d'eau</label>
          <select id="bf-type">
            <option value="pool" ${typeEau === "pool" ? "selected" : ""}>Piscine</option>
            <option value="spa"  ${typeEau === "spa"  ? "selected" : ""}>Spa</option>
          </select>
        </div>

        <div class="bsa-mb-form-group">
          <label>Désinfectant</label>
          <select id="bf-disinfectant">
            <option value="chlorine" ${disinf === "chlorine" ? "selected" : ""}>Chlore</option>
            <option value="bromine"  data-only="spa"  ${disinf === "bromine"  ? "selected" : ""}>Brome</option>
            <option value="salt"     data-only="pool" ${disinf === "salt"     ? "selected" : ""}>Sel</option>
          </select>
        </div>

        <div class="bsa-mb-form-group" id="bf-salt-row" style="${disinf === "salt" ? "" : "display:none"}">
          <label>Sel cible (ppm)</label>
          <input type="number" id="bf-salt-target" value="${b?.cible_de_sel_ppm || 3600}" min="0" max="10000">
        </div>

        <div class="bsa-mb-form-group" id="bf-chlorine-row" style="${disinf === "salt" ? "display:none" : ""}">
          <label>Type de chlore</label>
          <select id="bf-chlorine-type">
            <option value="pucks"    ${chlorineType === "pucks"    ? "selected" : ""}>Pucks / Tabs (chlore stabilisé)</option>
            <option value="granules" ${chlorineType === "granules" ? "selected" : ""}>Chlore en granules</option>
          </select>
        </div>

        <div class="bsa-mb-form-group">
          <label>Volume</label>
          <div style="display:flex;gap:8px;">
            <input type="number" id="bf-volume" style="flex:1" value="${b?.volume ? Math.round(parseFloat(b.volume)) : ""}" placeholder="40000" min="0">
            <select id="bf-unit" style="max-width:120px;">
              <option value="litres"  ${unit === "litres"  ? "selected" : ""}>Litres</option>
              <option value="gallons" ${unit === "gallons" ? "selected" : ""}>Gallons</option>
            </select>
          </div>
        </div>

        <div class="bsa-mb-form-group">
          <label>Type de filtre</label>
          <select id="bf-filter">
            <option value=""          ${!filtre ? "selected" : ""}>—</option>
            <option value="sand"      ${filtre === "sand"      ? "selected" : ""}>Sable</option>
            <option value="cartridge" ${filtre === "cartridge" ? "selected" : ""}>Cartouche</option>
            <option value="de"        ${filtre === "de"        ? "selected" : ""}>Diatomée</option>
          </select>
        </div>

        <div class="bsa-subsection" id="bf-piscine-block" style="${typeEau === "pool" ? "" : "display:none"}">
          <div class="bsa-subsection-title">Options piscine</div>

          <div class="bsa-mb-form-group">
            <label>Type de construction</label>
            <div class="bsa-pills" id="bf-construction-group">
              ${CONSTRUCTIONS.map((c) => `
                <button type="button" class="bsa-pill-btn${construction === c.val ? " active" : ""}" data-val="${c.val}">${c.label}</button>
              `).join("")}
            </div>
          </div>

          <div class="bsa-subsection-divider"></div>

          <div class="bsa-toggle-row">
            <label>Piscine couverte</label>
            <button type="button" class="bsa-toggle${couverte ? "" : " off"}" id="bf-couverte-toggle" aria-pressed="${couverte}"></button>
          </div>

          <div class="bsa-mb-form-group" id="bf-couverture-fields" style="${couverte ? "" : "display:none"}">
            <label>Type de couverture</label>
            <div class="bsa-pills" id="bf-couverture-group">
              ${COUVERTURES.map((c) => `
                <button type="button" class="bsa-pill-btn${couverture === c.val ? " active" : ""}" data-val="${c.val}">${c.label}</button>
              `).join("")}
            </div>
          </div>
        </div>

        <div class="bsa-toggle-row">
          <label>Bassin principal</label>
          <button type="button" class="bsa-toggle${principal ? "" : " off"}" id="bf-principal-toggle" aria-pressed="${principal}"></button>
        </div>
        <div class="bsa-toggle-row">
          <label>Ionisateur</label>
          <button type="button" class="bsa-toggle${ionisateur ? "" : " off"}" id="bf-ionisateur-toggle" aria-pressed="${ionisateur}"></button>
        </div>
        <div class="bsa-toggle-row">
          <label>Ozonateur</label>
          <button type="button" class="bsa-toggle${ozonateur ? "" : " off"}" id="bf-ozonateur-toggle" aria-pressed="${ozonateur}"></button>
        </div>

        <div class="bsa-mb-form-group">
          <label>Notes</label>
          <textarea id="bf-notes" rows="3" placeholder="Notes optionnelles...">${esc(b?.notes || "")}</textarea>
        </div>

        <div class="bsa-form-actions">
          <button type="button" class="bsa-btn-save" id="bf-save">${b ? "Sauvegarder" : "Créer le bassin"}</button>
          ${b ? `<a href="/pages/water-analyzer?bassin=${encodeURIComponent(b.id)}" class="bsa-btn-analyse-main">Lancer une analyse →</a>` : ""}
        </div>

        ${b ? `<button type="button" class="bsa-btn-danger-ghost" id="bf-delete">Supprimer ce bassin</button>` : ""}
      </div>
    `;
  }

  function renderMascotte() {
    const imgTag = mascotteUrl
      ? `<img src="${esc(mascotteUrl)}" alt="Mascotte Barracuda" class="bsa-mascotte-img" onerror="this.style.display='none'">`
      : "";
    return `
      <footer class="bsa-mascotte-footer">
        ${imgTag}
        <div class="bsa-mascotte-tagline">L'eau propre, c'est notre affaire.</div>
        <div class="bsa-mascotte-brand">Barracuda Piscines · Spas · Béton</div>
      </footer>
    `;
  }

  // ─── Events ───────────────────────────────────────────────────

  function renderAnalyseModal(a, bassinObj) {
    const score = a.score != null ? parseInt(a.score, 10) : null;
    const scoreClass = score == null ? 'info' : score >= 80 ? 'ok' : score >= 60 ? 'warn' : 'crit';
    const date = fmtDate(a.date_d_analyse || a.date || a.created_at);
    const waterType = bassinObj?.type_d_eau || 'pool';
    const etat = ETAT_LABELS[a.etat_eau] || { label: a.etat_eau || '—', cls: 'info' };
    const bassinName = bassinObj
      ? (bassinObj.nom_du_bassin || (bassinObj.type_d_eau === 'spa' ? 'Mon spa' : 'Ma piscine'))
      : '—';

    const params = waterType === 'spa'
      ? ['ph', 'alk', 'hardness', 'fc', 'br']
      : ['ph', 'alk', 'hardness', 'fc', 'cya'];

    const paramRows = params.map(key => {
      const idx = a[key];
      const value = getParamValue(key, idx);
      const band = getBand(key, value, waterType);
      const { label, unit } = ANALYSE_PARAM_LABELS[key] || { label: key, unit: '' };
      const t = (ANALYSE_TARGETS[waterType] || {})[key];
      const targetStr = t ? `${t.min}–${t.max}${unit ? ' ' + unit : ''}` : '—';
      const valueStr = value !== null ? `${value}${unit ? ' ' + unit : ''}` : '—';
      return `
        <div class="bsa-modal-param-row">
          <span class="bsa-modal-param-label">${esc(label)}</span>
          <span class="bsa-modal-param-value">${esc(valueStr)}</span>
          <span class="bsa-analyse-badge ${bandClass(band)}">${esc(bandLabel(band))}</span>
          <span class="bsa-modal-param-target">Cible: ${esc(targetStr)}</span>
        </div>
      `;
    }).join('');

    const dialHtml = (window.BSA_LSI && a.lsi != null && isFinite(parseFloat(a.lsi)))
      ? window.BSA_LSI.renderLsiDial(parseFloat(a.lsi))
      : '';

    return `
      <div class="bsa-modal-overlay" id="bsa-analyse-modal">
        <div class="bsa-modal-panel">
          <div class="bsa-modal-header">
            <div>
              <div class="bsa-modal-title">${esc(bassinName)}</div>
              <div class="bsa-modal-sub">${esc(date)}</div>
            </div>
            <button type="button" class="bsa-modal-close" id="bsa-modal-close-btn">✕</button>
          </div>
          <div class="bsa-modal-body">
            <div class="bsa-modal-top-row">
              ${dialHtml}
              <div class="bsa-modal-score-block">
                ${score != null ? `<div class="bsa-modal-score-num ${scoreClass}">${score}%</div><div class="bsa-modal-score-label">Qualité de l'eau</div>` : ''}
                <span class="bsa-analyse-badge ${etat.cls}">${esc(etat.label)}</span>
              </div>
            </div>
            <div class="bsa-modal-params">
              ${paramRows}
            </div>
            <div class="bsa-modal-footer-actions">
              <a href="/pages/water-analyzer?bassin=${encodeURIComponent(bassinObj?.id || '')}" class="bsa-btn-analyse-main">Nouvelle analyse →</a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function bindEvents() {
    container.querySelectorAll("[data-select-bassin]").forEach((el) => {
      el.addEventListener("click", (ev) => {
        if (ev.target.closest(".bsa-bassin-actions")) return;
        const id = el.dataset.selectBassin;
        if (id === selectedId && !isNewForm) return;
        selectedId = id;
        isNewForm = false;
        render();
      });
    });

    container.querySelectorAll("[data-edit-bassin]").forEach((el) => {
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        selectedId = el.dataset.editBassin;
        isNewForm = false;
        render();
      });
    });

    const addBtn = document.getElementById("bsa-add-bassin-btn");
    if (addBtn) {
      addBtn.addEventListener("click", () => {
        isNewForm = true;
        selectedId = null;
        render();
      });
    }

    const typeSel = document.getElementById("bf-type");
    if (typeSel) {
      typeSel.addEventListener("change", () => {
        const isPiscine = typeSel.value === "pool";
        const block = document.getElementById("bf-piscine-block");
        if (block) block.style.display = isPiscine ? "" : "none";

        filterDisinfectantOptions();
        updateSaltRow();
        updateChlorineRow();
      });
    }

    const disinfSel = document.getElementById("bf-disinfectant");
    if (disinfSel) disinfSel.addEventListener("change", () => { updateSaltRow(); updateChlorineRow(); });

    ["bf-principal-toggle", "bf-ionisateur-toggle", "bf-ozonateur-toggle"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener("click", () => {
          el.classList.toggle("off");
          el.setAttribute("aria-pressed", !el.classList.contains("off"));
        });
      }
    });

    const couverteToggle = document.getElementById("bf-couverte-toggle");
    if (couverteToggle) {
      couverteToggle.addEventListener("click", () => {
        couverteToggle.classList.toggle("off");
        const on = !couverteToggle.classList.contains("off");
        couverteToggle.setAttribute("aria-pressed", on);
        const fields = document.getElementById("bf-couverture-fields");
        if (fields) fields.style.display = on ? "" : "none";
      });
    }

    ["bf-construction-group", "bf-couverture-group"].forEach((groupId) => {
      const group = document.getElementById(groupId);
      if (!group) return;
      group.querySelectorAll(".bsa-pill-btn").forEach((pill) => {
        pill.addEventListener("click", () => {
          group.querySelectorAll(".bsa-pill-btn").forEach((p) => p.classList.remove("active"));
          pill.classList.add("active");
        });
      });
    });

    const saveBtn = document.getElementById("bf-save");
    if (saveBtn) saveBtn.addEventListener("click", handleSave);

    const deleteBtn = document.getElementById("bf-delete");
    if (deleteBtn) {
      deleteBtn.addEventListener("click", () => {
        if (confirm("Supprimer ce bassin? Cette action est irréversible.")) {
          deleteBassin(selectedId);
        }
      });
    }

    container.querySelectorAll("[data-open-analyse]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        const id = el.dataset.openAnalyse;
        if (!id) return;
        window.location.href = `/pages/water-analyzer?analyse=${encodeURIComponent(id)}`;
      });
    });

    filterDisinfectantOptions();
  }

  function closeAnalyseModal() {
    document.getElementById('bsa-analyse-modal')?.remove();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onEscModal);
  }

  function onEscModal(e) {
    if (e.key === 'Escape') closeAnalyseModal();
  }

  function updateSaltRow() {
    const saltRow = document.getElementById("bf-salt-row");
    const disinfSel = document.getElementById("bf-disinfectant");
    if (saltRow && disinfSel) {
      saltRow.style.display = disinfSel.value === "salt" ? "" : "none";
    }
  }

  // Type de chlore visible UNIQUEMENT si désinfectant ≠ sel (inverse de la row sel).
  function updateChlorineRow() {
    const row = document.getElementById("bf-chlorine-row");
    const disinfSel = document.getElementById("bf-disinfectant");
    if (row && disinfSel) {
      row.style.display = disinfSel.value === "salt" ? "none" : "";
    }
  }

  function filterDisinfectantOptions() {
    const typeSel = document.getElementById("bf-type");
    const disinfSel = document.getElementById("bf-disinfectant");
    if (!typeSel || !disinfSel) return;

    const currentType = typeSel.value;

    for (const opt of disinfSel.options) {
      const only = opt.dataset.only;
      if (!only) continue;
      const shouldHide = only !== currentType;
      opt.hidden = shouldHide;
      opt.disabled = shouldHide;
    }

    const selectedOpt = disinfSel.options[disinfSel.selectedIndex];
    if (selectedOpt && selectedOpt.hidden) {
      disinfSel.value = "chlorine";
      disinfSel.dispatchEvent(new Event("change"));
    }
  }

  // ─── Actions ──────────────────────────────────────────────────

  async function handleSave() {
    const saveBtn = document.getElementById("bf-save");
    saveBtn.disabled = true;
    saveBtn.textContent = "Enregistrement...";

    const getActivePill = (groupId) =>
      document.querySelector(`#${groupId} .bsa-pill-btn.active`)?.dataset.val || "";
    const getToggleOn = (id) =>
      !document.getElementById(id)?.classList.contains("off");

    const typeEau = document.getElementById("bf-type")?.value || "pool";
    const isPiscine = typeEau === "pool";
    const couverte = getToggleOn("bf-couverte-toggle");
    const volume = parseFloat(document.getElementById("bf-volume")?.value || "0");
    const unit = document.getElementById("bf-unit")?.value || "litres";
    const volumeLitres = unit === "gallons" ? volume * 3.78541 : volume;

    // Type de chlore → produits_utilises_json (parité POS). Préserve d'éventuelles autres clés (ne remplace que
    // les clés de type de chlore). Sel : aucune clé de type de chlore.
    const curBassin = bassins.find((x) => x.id === selectedId);
    const basePU = parseProductsUsedKeys(curBassin?.produits_utilises_json ?? curBassin?.produits_utilises)
      .filter((k) => !CHLORINE_TYPE_KEYS.includes(k));
    const chlorineKey = (document.getElementById("bf-chlorine-type")?.value === "granules") ? "granules_non_stab" : "pucks_trichlor";
    const disinfectantVal = document.getElementById("bf-disinfectant")?.value || "chlorine";
    const productsUsedArr = disinfectantVal === "salt" ? basePU : basePU.concat(chlorineKey);

    const fields = {
      nom_du_bassin:              document.getElementById("bf-name")?.value || "",
      type_d_eau:                 typeEau,
      desinfectant:               document.getElementById("bf-disinfectant")?.value || "chlorine",
      volume:                     String(Math.round(volume)),
      unite_de_volume:            unit,
      volume_normalise_en_litres: String(Math.round(volumeLitres)),
      tpe_de_filtre:              document.getElementById("bf-filter")?.value || "",
      ionisateur:                 getToggleOn("bf-ionisateur-toggle") ? "true" : "false",
      ozonateur:                  getToggleOn("bf-ozonateur-toggle") ? "true" : "false",
      bassin_principal:           getToggleOn("bf-principal-toggle") ? "true" : "false",
      notes:                      document.getElementById("bf-notes")?.value || "",
      actif:                      "true",
      // 3 champs piscine — vides si Spa pour tenter un clear côté Shopify
      type_construction:          isPiscine ? getActivePill("bf-construction-group") : "",
      piscine_couverte:           isPiscine ? (couverte ? "true" : "false") : "",
      type_couverture:            (isPiscine && couverte) ? getActivePill("bf-couverture-group") : "",
      // Type de chlore (parité POS) — string JSON, "[]" pour le sel.
      produits_utilises_json:     JSON.stringify(productsUsedArr),
    };

    if (fields.desinfectant === "salt") {
      fields.cible_de_sel_ppm = document.getElementById("bf-salt-target")?.value || "3600";
    }

    if (bassins.length === 0 && isNewForm) {
      fields.bassin_principal = "true";
    }

    const runSave = (payload) =>
      isNewForm
        ? window.BSA_API.createBassin(payload)
        : window.BSA_API.updateBassin(selectedId, payload);

    try {
      let saved;
      try {
        saved = await runSave(fields);
      } catch (firstErr) {
        // Si Shopify rejette l'empty string sur les 3 champs piscine, retry en les omettant.
        const msg = String(firstErr?.message || "").toLowerCase();
        const hitsPoolField = POOL_KEYS.some((k) => msg.includes(k));
        if (!hitsPoolField) throw firstErr;
        console.warn("[BSA] Retry sans les champs piscine vides :", firstErr.message);
        const retry = { ...fields };
        for (const k of POOL_KEYS) delete retry[k];
        saved = await runSave(retry);
      }

      bassins = await window.BSA_API.getBassins();

      if (fields.bassin_principal === "true" && saved?.id) {
        const others = bassins.filter((x) => x.id !== saved.id && x.bassin_principal === "true");
        if (others.length) {
          await Promise.all(others.map((x) =>
            window.BSA_API.updateBassin(x.id, { bassin_principal: "false" })
          ));
          bassins = await window.BSA_API.getBassins();
        }
      }

      selectedId = saved?.id || selectedId;
      isNewForm = false;
      render();
    } catch (e) {
      saveBtn.textContent = "Erreur — Réessayer";
      saveBtn.disabled = false;
      console.error("[BSA] Save error:", e);
    }
  }

  async function deleteBassin(id) {
    if (!id) return;
    try {
      await window.BSA_API.deleteBassin(id);
      bassins = bassins.filter((b) => b.id !== id);
      selectedId = bassins[0]?.id || null;
      isNewForm = bassins.length === 0;
      render();
    } catch (e) {
      console.error("[BSA] Delete error:", e);
      alert("Erreur à la suppression.");
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────

  function esc(str) {
    const div = document.createElement("div");
    div.textContent = str == null ? "" : String(str);
    return div.innerHTML;
  }

  function fmtDate(raw) {
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" });
  }

  function showLoading() {
    container.innerHTML = '<div class="bsa-loading"><div class="bsa-spinner"></div></div>';
  }

  // ─── Boot ─────────────────────────────────────────────────────

  init();
})();
